import React from 'react'
import { createRoot } from 'react-dom/client'
import 'what-input'

import { BasicViewPaths } from '../common/types.js'
import { errorHasMessage } from '../common/utils/utils.js'
import { createApi } from './api.js'
import { enableScroll } from './components/helper/index.js'
import { NotificationProps } from './components/notification/notification.js'
import i18n from './i18n.js'
import { initData } from './init-data.js'
import { Subscription } from './models/subscription.js'
import { getRootProps } from './props/root.js'
import { isIFrameValidationUpdate, onValidationMessageUpdate } from './props/subscription.js'
import { Root } from './root.js'
import { Api, FlowTypes, IFrameValidationMessage, State, View } from './types.js'
import { capturePageview } from './utils/analytics.js'
import { handleLegacyRoute } from './utils/handle-legacy-route.js'
import { UpsellOptionType } from './views/portal/cancel/cancel.js'

import './index.scss'

window.addEventListener('load', () => {
    // Run before parsing hash
    handleLegacyRoute()

    const container = document.querySelector('#root')!

    const getInitialNotifications = (searchQuery?: URLSearchParams): NotificationProps[] => {
        if (searchQuery?.get('notify')) {
            return [
                {
                    children: i18n.t(searchQuery.get('notify') + ''),
                    isDefaultOpen: true,
                    isClosable: true,
                    duration: 10000,
                },
            ]
        }

        return []
    }

    const getDebugOptions = (searchQuery?: URLSearchParams) => {
        return {
            language: searchQuery?.get('debug-lang') ? searchQuery.get('debug-lang') + '' : undefined,
        }
    }

    const getAndCleanSearchQuery = () => {
        if (window.location.search) {
            const query = window.location.search
            const newUrl = `${window.location.origin}/${window.location.hash}`
            window.history.pushState({ path: newUrl }, '', newUrl)

            return new URLSearchParams(query)
        }

        return undefined
    }

    const searchQuery = getAndCleanSearchQuery()

    const initialState: State = {
        isLoading: true,
        isDebugEnabled: !!process.env.APP_DEBUG,
        baseUrl: process.env.APP_CLIENT_BASE_URL!,
        apiBaseUrl: `${process.env.APP_API_BASE_URL!}/api`,
        notificationList: {
            items: getInitialNotifications(searchQuery),
        },
        formData: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            postalCode: '',
            address: '',
            city: '',
            country: '',
            personalNumber: '',
            authenticationMethod: '',
            shippingMethodUuid: '',
            differentShippingAddress: false,
            shippingAddress: '',
            shippingCity: '',
            shippingPostalCode: '',
            // TEMP: Lock shipping if defined by scrive
            postalCodeLock: false,
            addressLock: false,
            cityLock: false,
        },
        formErrors: {},
        selectedPlan: '',
        selectedPlans: [],
        validation: {
            timeout: 0,
            isInitiated: false,
            isChecking: false,
            isLoading: false,
        },
        portal: {
            header: {
                isOpen: false,
            },
            authCheckStarted: false,
            newNickname: '',
            subscriptionClaimFiles: {
                frontSideDamageFiles: [],
                backSideDamageFiles: [],
            },
            upsellChoice: UpsellOptionType.SWAP,
            subscriptionClaim: {
                termsAndConditions: false,
                deviceDamaged: false,
                dateOfDamage: new Date().toISOString(),
                frontSideDamage: false,
                backSideDamage: false,
                frontSideDamageFiles: [],
                backSideDamageFiles: [],
            },
        },
        debugOptions: getDebugOptions(searchQuery),
    }

    const root = createRoot(container)

    const view: View = {
        state: initialState,
        searchQuery,
        update: () => {
            const props = getRootProps(view)
            root.render(<Root {...props} />)

            // TEMP: Hack to enable scrolling after React.render
            window.setTimeout(() => {
                window.requestAnimationFrame(() => {
                    const hasOpenModal = document.querySelector('.modal.is-open')
                    if (!hasOpenModal) {
                        enableScroll()
                    }
                })
            })
        },
        back: () => {
            window.history.back()
            window.dispatchEvent(new HashChangeEvent('hashchange'))
        },
        addNotification: (notification) => {
            view.state.notificationList = {
                items: [...view.state.notificationList.items, notification],
            }
            view.update()
        },
        updateSubscription: (data, triggerUpdate = true, products) => {
            try {
                const subscription: Subscription = new Subscription(data, products)
                const method = subscription.possibleSubscriptionMethods?.[0]

                if (!method) {
                    throw new Error('No subscription method found')
                }

                const getFlowPicked = (): FlowTypes | undefined => {
                    switch (localStorage.getItem('flowPicked-' + subscription.uuid)) {
                        case 'new-user':
                            return 'new-user'
                        case 'existing-user':
                            return 'existing-user'
                        default:
                            return undefined
                    }
                }

                view.state.formData = {
                    firstName: subscription.customer?.firstName || view.state.formData.firstName,
                    lastName: subscription.customer?.lastName || view.state.formData.lastName,
                    email: subscription.customer?.email || view.state.formData.email,
                    phone: subscription.customer?.phone || view.state.formData.phone,
                    postalCode: subscription.customer?.postalCode || view.state.formData.postalCode,
                    address: subscription.customer?.address || view.state.formData.address,
                    city: subscription.customer?.city || view.state.formData.city,
                    country: subscription.shippingCountry,
                    personalNumber: subscription.customer?.personalNumber || view.state.formData.personalNumber,
                    authenticationMethod: `${method.providerName}_${method.bankName || ''}`,
                    shippingMethodUuid: subscription.deliveryMethod?.uuid || '',
                    differentShippingAddress: subscription.differentShippingAddress,
                    shippingPostalCode: subscription.shippingPostalCode || view.state.formData.shippingPostalCode,
                    shippingAddress: subscription.shippingAddress || view.state.formData.shippingAddress,
                    shippingCity: subscription.shippingCity || view.state.formData.shippingCity,
                    // TEMP: Lock shipping info if defined
                    postalCodeLock: !subscription.customer?.canEditPostalCode,
                    addressLock: !subscription.customer?.canEditAddress,
                    cityLock: !subscription.customer?.canEditCity,
                    marketingTerms: !!subscription.customer?.marketingTerms,
                }
                view.state.flowPicked = getFlowPicked()
                view.state.subscription = subscription
                // we always want to reset loading state after subscription update
                view.state.isLoading = false

                if (triggerUpdate) {
                    view.update()
                }
            } catch (error) {
                if (errorHasMessage(error)) {
                    view.addNotification({
                        children: i18n.t(error.message),
                        isDefaultOpen: true,
                        type: 'error',
                    })
                }
                window.location.hash = `#/${BasicViewPaths.GENERAL_ERROR}`
                view.update()
            }
        },
        api: null as unknown as Api, // Hack for circular dependency
        initValidationTimeout: () => {
            if (!view.state.validation.isInitiated) {
                void view.api.pollValidationStatus()
                view.state.validation.isInitiated = true
                view.update()
            }
        },
    }

    view.api = createApi(view)

    window.addEventListener('message', function (message) {
        if (typeof message.data === 'string') {
            try {
                const data = JSON.parse(message.data)

                if (isIFrameValidationUpdate(data)) {
                    onValidationMessageUpdate(view, data)
                }
            } catch (e) {
                // do nothing
            }
        }
    })

    if (window.parent && window.location !== window.parent.location) {
        window.parent.postMessage(JSON.stringify({ message: IFrameValidationMessage.RELOAD }), '*')
    }

    // Initial render
    view.update()

    capturePageview()
    window.addEventListener('hashchange', () => {
        capturePageview()
        view.update()
    })

    initData(view).catch((error) => console.error(error))
})
